<template>
	<v-dialog
		v-if="item"
		v-model="showDialog"
		max-width="820"
		scrollable
		persistent
	>
		<v-card>
			<v-card-title> Form Fields </v-card-title>
			<v-card-text>
				<v-form ref="form">
					<v-row no-gutters class="mt-0">
						<v-col class="pa-0">
							<v-select
								v-model="selectedGridViewFieldsIds"
								multiple
								chips
								deletable-chips
								:allow-overflow="false"
								label="Field"
								:items="
									filteredGridViewFields.sort((a, b) =>
										a.Label > b.Label ? 1 : -1
									)
								"
								item-text="Label"
								item-value="Id"
								:rules="rules.requiredOne"
							/>
						</v-col>
					</v-row>

					<v-row no-gutters class="mt-0">
						<v-col class="pa-0">
							<v-text-field
								v-model="label"
								label="Label"
								:rules="rules.required"
							/>
						</v-col>
						<v-col
							v-if="
								selectedGridViewFieldsIds &&
								selectedGridViewFieldsIds.length > 1
							"
							class="pl-2"
							cols="2"
						>
							<v-text-field
								v-model="item.Separator"
								label="Value separator"
								:rules="rules.required"
							/>
						</v-col>
					</v-row>
					<v-row no-gutters class="mt-0">
						<v-col class="pa-0">
							<v-checkbox
								hide-details
								v-model="item.CanIgnoreErrors"
								label="Can ignore errors"
							/>
						</v-col>
						<v-col class="pa-0">
							<v-checkbox
								hide-details
								v-model="item.Required"
								label="Required"
							/>
						</v-col>
						<v-col class="pa-0">
							<v-checkbox
								hide-details
								v-model="item.ForceChoice"
								label="Force choice"
							/>
						</v-col>
						<v-col class="pa-0">
							<v-checkbox
								:disabled="isPk"
								hide-details
								v-model="item.PrimaryKey"
								label="Primary Key"
							/>
						</v-col>
						<v-col class="pa-0">
							<v-checkbox
								hide-details
								class="mt-2"
								v-model="item.AllowUserToRetainValue"
								label="Allow user to retain value"
							/>
						</v-col>
					</v-row>
					<v-row no-gutters class="mt-0">
						<v-tooltip
							v-if="disableHidden.value"
							top
							right
							:open-on-click="true"
							:open-on-focus="true"
							:open-on-hover="true"
						>
							<template v-slot:activator="{ on, attrs }">
								<div v-bind="attrs" v-on="on">
									<v-checkbox
										:disabled="disableHidden.value"
										:off-icon="'mdi-information-outline'"
										color="primary"
										class="mt-0"
										v-model="hidden"
										label="Hidden"
									/>
								</div>
							</template>
							<span>{{ disableHidden.message }}</span>
						</v-tooltip>
						<v-col class="pa-0">
							<v-checkbox
								v-if="!disableHidden.value"
								class="mt-0"
								v-model="hidden"
								label="Hidden"
							>
								<template v-slot:prepend>
									<div v-if="disableHidden.value">
										<v-tooltip
											top
											:open-on-click="true"
											:open-on-focus="true"
											:open-on-hover="true"
										>
											<template
												v-slot:activator="{ on, attrs }"
											>
												<v-icon
													color="primary"
													v-bind="attrs"
													v-on="on"
													>mdi-information-outline</v-icon
												>
											</template>
											{{ disableHidden.message }}
										</v-tooltip>
									</div>
								</template>
							</v-checkbox>
						</v-col>
						<v-col class="pa-0">
							<v-checkbox
								class="mt-0"
								v-model="item.IsVisible"
								label="Visible on load"
							/>
						</v-col>
						<v-col>
							<v-checkbox
								class="mt-0"
								v-model="item.Readonly"
								label="Read-only"
							/>
						</v-col>
						<v-col>
							<v-checkbox
								class="mt-0"
								v-model="item.Uppercase"
								label="Uppercase"
							/>
						</v-col>
						<v-col>
							<v-checkbox
								class="mt-0"
								v-model="item.MobileHeader"
								label="Use as header in mobile app"
							/>
						</v-col>
					</v-row>

					<v-row no-gutters class="mt-0">
						<v-col class="pa-0">
							<v-row>
								<v-select
									v-if="!hidden"
									v-model="item.OnScanEdit"
									:allow-overflow="false"
									label="When loading an existing record via barcode scan (i.e. 'scan load')"
									:items="onScanEditItems"
									item-text="Title"
									item-value="Id"
									:rules="rules.required"
								>
									<template v-slot:item="data">
										<template v-if="isObject(data.item)">
											<v-list-item-content>{{
												data.header
											}}</v-list-item-content>
										</template>
										<template v-else>
											<v-list-item-content>
												<v-list-item-title>{{
													data.item.Title
												}}</v-list-item-title>
												<v-list-item-subtitle>{{
													data.item.Subtitle
												}}</v-list-item-subtitle>
											</v-list-item-content>
										</template>
									</template>
								</v-select>
							</v-row>
							<v-row>
								<v-select
									v-if="hidden"
									v-model="item.HiddenBehaviour"
									:allow-overflow="false"
									label="Hidden Behaviour"
									:items="hiddenBehaviourItems"
									item-text="Title"
									item-value="Id"
									:rules="rules.required"
								>
									<template v-slot:item="data">
										<template v-if="isObject(data.item)">
											<v-list-item-content>{{
												data.header
											}}</v-list-item-content>
										</template>
										<template v-else>
											<v-list-item-content>
												<v-list-item-title>{{
													data.item.Title
												}}</v-list-item-title>
												<v-list-item-subtitle>{{
													data.item.Subtitle
												}}</v-list-item-subtitle>
											</v-list-item-content>
										</template>
									</template>
								</v-select>
							</v-row>
						</v-col>
					</v-row>
				</v-form>
				<h5 class="mb-2">Custom Validation Rules</h5>
				<v-expansion-panels v-model="expansionPanel" inset>
					<form-field-validation-rule
						:ref="'formFieldValidationRule_' + index"
						v-for="(rule, index) in item.FormFieldRules"
						v-bind:key="index"
						:rule="rule"
						:formFieldIds="item.GridViewFieldIds"
						@remove="onFormValidationRuleRemove"
					/>
				</v-expansion-panels>

				<v-row no-gutters class="mt-2">
					<v-btn
						small
						class="primary"
						width="100%"
						@click.stop="addRule()"
						>Add Rule</v-btn
					>
				</v-row>
			</v-card-text>
			<v-divider />
			<v-card-actions>
				<v-spacer />
				<v-btn @click.stop="onClose">Close</v-btn>
				<v-btn color="primary" @click.stop="onSave">
					<v-icon class="mr-2">mdi-content-save</v-icon>
					Save
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>
<script>
import ConfirmDialog from '@/components/core/dialogs/confirmDialog';
import FormFieldValidationRule from './FormFieldValidationRule.vue';
import { FieldDataTypes } from '../../../../enums/fieldDataTypes';
import { formFieldOnScanEdit } from '../../../../enums/formFieldOnScanEdit';
import { formFieldHiddenBehaviour } from '../../../../enums/formFieldHiddenBehaviour';
import { FormFieldsGetters } from '../../../../store/getters';
import { FormFieldsActions } from '../../../../store/actions';
import { mapGetters } from 'vuex';

export default {
	name: 'FormFieldModal',
	components: { FormFieldValidationRule },
	props: {
		showDialog: Boolean,
	},
	computed: {
		inventoryFormId() {
			return this.$route.params.id;
		},

		...mapGetters({
			gridViewFields: [FormFieldsGetters.AVAILABLE_GRIDVIEW_FIELDS],
			selectedItem: [FormFieldsGetters.SELECTED_FIELD],
			selectedItemInteractions: [
				FormFieldsGetters.SELECTED_FIELD_INTERACTIONS,
			],
		}),
		disableHidden() {
			if (
				this.selectedItemInteractions.Actions.length > 0 &&
				this.selectedItemInteractions.Conditions.length > 0
			)
				return {
					value: true,
					message:
						"Can't hide a field with actions and conditions defined!",
				};
			if (this.selectedItemInteractions.Actions.length > 0)
				return {
					value: true,
					message: "Can't hide a field with actions defined!",
				};
			if (this.selectedItemInteractions.Conditions.length > 0)
				return {
					value: true,
					message: "Can't hide a field with actions defined!",
				};
			return { value: false };
		},
		filteredGridViewFields() {
			if (!this.selectedItem) return [];
			if (!this.gridViewFields) return [];
			if (this.selectedGridViewFieldsIds.length > 0) {
				let result = [];
				let selectedGridViewField = this.gridViewFields.find(
					(x) => x.Id == this.selectedGridViewFieldsIds[0]
				);
				if (selectedGridViewField) {
					result.push(selectedGridViewField);
					if (selectedGridViewField.LinkField) {
						result = this.gridViewFields.filter(
							(x) =>
								x.LinkFieldId ==
								selectedGridViewField.LinkFieldId
						);
					}
				}
				return result;
			} else return this.gridViewFields;
		},
		isPk() {
			if (this.selectedItem.GridViewFields) {
				if (this.selectedItem.GridViewFields[0].LinkField) return true;
				if (this.selectedItem.GridViewFields[0].ReferenceListType)
					return true;
				switch (
					this.selectedItem.GridViewFields[0].DataField.DataType
				) {
					case FieldDataTypes.AUTOGENERATED_NUMBER:
						return true;
					case FieldDataTypes.AUTOGENERATED_UNIQUEIDENTIFIER:
						return true;
					case FieldDataTypes.AUTOGENERATED_DATETIME:
						return true;
					case FieldDataTypes.AUTOGENERATED_BOOLEAN:
						return true;
				}
			}
			if (this.selectedGridViewFieldsIds.length == 1) {
				let field = this.gridViewFields.find(
					(g) => g.Id == this.selectedGridViewFieldsIds[0]
				);
				if (field)
					switch (
						this.gridViewFields.find(
							(g) => g.Id == this.selectedGridViewFieldsIds[0]
						).DataField.DataType
					) {
						case FieldDataTypes.AUTOGENERATED_NUMBER:
							return true;
						case FieldDataTypes.AUTOGENERATED_UNIQUEIDENTIFIER:
							return true;
						case FieldDataTypes.AUTOGENERATED_DATETIME:
							return true;
						case FieldDataTypes.AUTOGENERATED_BOOLEAN:
							return true;
					}
			}
			return false;
		},
	},
	watch: {
		selectedGridViewFieldsIds: function (newVal, oldVal) {
			if (!newVal) return;
			if (!oldVal) return;
			if (this.label && newVal.length != 0) return;
			let oldLabel = null;
			oldVal.forEach((v) => {
				let gv = this.gridViewFields.find((x) => x.Id == v);
				if (!oldLabel) {
					oldLabel = gv.Label;
				} else {
					oldLabel += ' / ' + gv.Label;
				}
			});

			if (this.label && oldLabel && this.label != oldLabel) return;

			let newLabel = null;
			newVal.forEach((v) => {
				let gv = this.gridViewFields.find((x) => x.Id == v);
				if (!newLabel) {
					newLabel = gv.Label;
				} else {
					newLabel += ' / ' + gv.Label;
				}
			});
			this.label = newLabel;
		},
		'item.HiddenBehaviour': function (newVal) {
			if (newVal != 0) {
				this.hidden = true;
			}
		},
		'item.Readonly': function (newVal) {
			if (newVal == 1) this.item.Required = false;
		},
		'item.Required': function (newVal) {
			if (newVal == 1) this.item.Readonly = false;
		},
	},
	data() {
		return {
			selectedGridViewFieldsIds: [],
			item: null,
			label: null,
			expansionPanel: null,
			rules: {
				required: [(value) => !!value || 'Field is required'],
				requiredOne: [
					(value) => {
						if (!value) return 'Field is required';
						if (value.length == 0) return 'Field is required';
						return true;
					},
				],
			},
			hidden: false,
			onScanEditItems: [
				{
					Id: formFieldOnScanEdit.OnlyOverwriteEmptyInput,
					Title: 'Only overwrite empty field',
					Subtitle:
						'If a field has preexisting values, it will not be overwritten upon scan load.',
				},
				{
					Id: formFieldOnScanEdit.AlwaysOverwriteInput,
					Title: 'Always overwrite field',
					Subtitle:
						'Field will always be overwritten upon scan load.',
				},
				{
					Id: formFieldOnScanEdit.AlwaysOverwriteInputExceptBlanks,
					Title: 'Always overwrite field, except with blank or NULL',
					Subtitle:
						'Field will be overwritten upon scan load unless incoming field is blank or NULL.',
				},
				{
					Id: formFieldOnScanEdit.NeverOverwriteInput,
					Title: 'Never overwrite field',
					Subtitle: 'Field is never overwritten upon scan load.',
				},
			],
			hiddenBehaviourItems: [
				{
					Id: formFieldHiddenBehaviour.AlwaysOverwrite,
					Title: 'Always overwrite input',
					Subtitle:
						'Value from the field is updated when a record is found from scan',
				},
				{
					Id: formFieldHiddenBehaviour.OverWriteIfEmpty,
					Title: 'Overwrite if empty',
					Subtitle: '',
				},
				{
					Id: formFieldHiddenBehaviour.OverwriteWhenRequired,
					Title: 'Overwrite when required',
					Subtitle:
						'This will work in combination with required_if eg SecondAtempt will be required if FirstAtempt has value',
				},
			],
		};
	},
	mounted() {
		this.$store
			.dispatch(
				FormFieldsActions.LOAD_GRIDVIEW_FIELDS,
				this.inventoryFormId
			)
			.then(() =>
				this.$store.dispatch(
					FormFieldsActions.LOAD_INTERACTIONS,
					this.inventoryFormId
				)
			)
			.then(() => {
				this.item = this.selectedItem;
				if (!this.item.Id) {
					this.item.IsVisible = true;
				}
				this.selectedGridViewFieldsIds =
					this.selectedItem.GridViewFieldIds || [];
				this.label = this.item.Label;
			});
	},
	methods: {
		closeAllPanels() {
			this.expansionPanel = null;
		},
		openPanel(index) {
			this.expansionPanel = index;
		},

		validate() {
			let valid = true;
			valid = valid && this.$refs.form.validate();
			this.item.FormFieldRules.forEach((rule) => {
				var index = this.item.FormFieldRules.indexOf(rule);
				if (this.$refs['formFieldValidationRule_' + index]) {
					let ruleValid =
						this.$refs[
							'formFieldValidationRule_' + index
						][0].validate();
					valid = valid && ruleValid;
				}
			});
			return valid;
		},
		onSave() {
			if (!this.validate()) return;
			this.item.GridViewFieldIds = this.selectedGridViewFieldsIds;
			this.item.Label = this.label;
			if (this.item.Id) {
				this.$store
					.dispatch(FormFieldsActions.UPDATE, this.item)
					.then((resp) => {
						if (!resp) return;
						this.$emit('close');
					});
			} else {
				this.$store
					.dispatch(FormFieldsActions.ADD, this.item)
					.then((resp) => {
						if (!resp) return;
						this.$emit('close');
					});
			}
		},
		onClose() {
			this.$emit('close');
		},
		addRule() {
			this.item.FormFieldRules.push({});
			this.openPanel(this.item.FormFieldRules.length - 1);
		},
		onFormValidationRuleRemove(item) {
			ConfirmDialog({
				message:
					'Are you sure you want to delete this validation rule?',
			}).then((isDelete) => {
				if (!isDelete) return;
				let index = this.item.FormFieldRules.indexOf(item);
				if (index > -1) {
					this.item.FormFieldRules.splice(index, 1);
				}
			});
		},
		isObject(item) {
			return typeof item !== 'object';
		},
	},
};
</script>
